import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';

const TradeModal = ({ symbol, onClose, onTradeComplete }) => {
  const [formData, setFormData] = useState({
    quantity: '',
    price_per_share: '',
    transaction_type: 'BUY',
    order_type: 'market',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState('');
  const [marketStatus, setMarketStatus] = useState(null);
  const [marketStatusLoading, setMarketStatusLoading] = useState(true);

  // Fetch market status
  useEffect(() => {
    let isMounted = true;
    const fetchMarketStatus = async () => {
      try {
        const response = await api.get('/trading/orders/market_status/');
        if (isMounted && response.data) {
          setMarketStatus(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch market status:', error);
      } finally {
        if (isMounted) setMarketStatusLoading(false);
      }
    };
    fetchMarketStatus();
  }, []);

  // Fetch current price, auto-update every 25s
  useEffect(() => {
    if (!symbol) return;
    let isMounted = true;
    const baseSymbol = symbol.endsWith('.L') ? symbol.replace('.L', '') : symbol;
    const fetchPrice = () => {
      setPriceLoading(true);
      setPriceError('');
      fetch(`${process.env.REACT_APP_URL || 'http://localhost:8000'}/api/v1/market-data/company-profile/?symbol=${baseSymbol}`)
        .then(res => res.json())
        .then(data => {
          if (isMounted && data && data.price !== undefined) {
            setCurrentPrice(data.price);
          }
        })
        .catch(() => {
          if (isMounted) setPriceError('Failed to fetch current price.');
        })
        .finally(() => {
          if (isMounted) setPriceLoading(false);
        });
    };
    fetchPrice();
    const interval = setInterval(fetchPrice, 25000);
    return () => { isMounted = false; clearInterval(interval); };
  }, [symbol]);

  // Auto-fill price_per_share for market order and auto-update every 25s
  useEffect(() => {
    if (formData.order_type === 'market' && currentPrice !== null) {
      setFormData(prev => ({ ...prev, price_per_share: Number(currentPrice).toFixed(4) }));
    }
    // If switching to limit, clear price_per_share
    if (formData.order_type === 'limit') {
      setFormData(prev => ({ ...prev, price_per_share: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.order_type, currentPrice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quantity || !formData.price_per_share) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }
    try {
      setLoading(true);
      if (formData.order_type === 'limit') {
        // Place a limit order (goes to Pending Orders)
        const side = formData.transaction_type.toLowerCase(); // 'buy' | 'sell'
        const payload = {
          symbol: symbol,
          side: side, 
          quantity: parseInt(formData.quantity),
          // Clamp to 4 decimal places to satisfy backend validator
          limit_price: parseFloat(Number(formData.price_per_share).toFixed(4)),
        };
        const response = await api.post('/trading/orders/', payload);
        setMessage({ type: 'success', text: 'Limit order placed. Check Pending Orders.' });
        if (onTradeComplete) onTradeComplete();
        setTimeout(() => { onClose(); }, 1500);
      } else {
        // Market order executes immediately
        // Do NOT send price_per_share; backend will use live price and execute
        const response = await api.post('/trading/trade/execute/', {
          symbol: symbol,
          quantity: parseInt(formData.quantity),
          transaction_type: formData.transaction_type,
        });
        if (response.data.success) {
          setMessage({ type: 'success', text: response.data.message });
          if (onTradeComplete) onTradeComplete();
          setTimeout(() => { onClose(); }, 2000);
        } else {
          setMessage({ type: 'error', text: response.data.error });
        }
      }
    } catch (error) {
      let backendError = error?.response?.data?.error;
      setMessage({ type: 'error', text: backendError || 'Trade execution failed' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Only allow user to edit price_per_share if limit order
    if (name === 'price_per_share' && formData.order_type === 'market') return;
    
    // Limit price_per_share to 4 decimal places
    if (name === 'price_per_share') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        const limitedValue = numericValue.toFixed(4);
        setFormData({
          ...formData,
          [name]: limitedValue
        });
        return;
      }
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  if (!symbol) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Trade {symbol}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        {/* Market Hours Warning */}
        {!marketStatusLoading && marketStatus && !marketStatus.market_open && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700">
            <div className="flex items-center space-x-2">
              <div className="text-sm font-medium">
                ⚠️ Market is currently closed
              </div>
            </div>
            <div className="text-xs mt-1">
              Orders can only be placed during market hours (Mon-Fri 8:00 AM - 4:30 PM London time)
            </div>
          </div>
        )}

        {message.text && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'
          }`}>
            {message.text}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Transaction Type
            </label>
            <select
              name="transaction_type"
              value={formData.transaction_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="BUY">Buy</option>
              <option value="SELL">Sell</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Order Type
            </label>
            <select
              name="order_type"
              value={formData.order_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="market">Market Order</option>
              <option value="limit">Limit Order</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Quantity
            </label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Number of shares"
              min="1"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Price per Share (£)
            </label>
            <input
              type="number"
              name="price_per_share"
              value={formData.price_per_share}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="0.0000"
              step="0.0001"
              min="0.0001"
              required
              disabled={formData.order_type === 'market'}
            />
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Total: {formatCurrency(formData.quantity * formData.price_per_share)}
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (!marketStatusLoading && marketStatus && !marketStatus.market_open)}
              className={`flex-1 px-4 py-2 rounded-md font-medium text-white ${
                formData.transaction_type === 'BUY'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? 'Processing...' : (!marketStatusLoading && marketStatus && !marketStatus.market_open) ? 'Market Closed' : `${formData.transaction_type} Stock`}
            </button>
          </div>
        </form>
        {/* Current Market Price Info */}
        <div className="mt-6 p-3 bg-gray-50 dark:bg-gray-900 rounded text-sm text-gray-800 dark:text-gray-200">
          <span className="font-semibold">Current Market Price: </span>
          {priceLoading ? 'Loading...' : priceError ? <span className="text-red-600">{priceError}</span> : currentPrice !== null ? <span>£{Number(currentPrice).toFixed(2)}</span> : 'N/A'}
          <span className="ml-2 text-xs text-gray-400">(auto-updates every 25s)</span>
        </div>
      </div>
    </div>
  );
};

export default TradeModal; 